import { useEffect, useState } from 'react';

import cn from 'classnames';
import { buildNavPath as buildNavPathHelper } from 'helpers/candidate_application/form-page-helpers';
import { getCheckTypeIcon } from 'helpers/iconsHelper';
import { useQueryParams } from 'hooks/query-params';
import CandidatePage from 'layouts/CandidatePage/CandidatePage';
import { AnimatePresence } from 'motion/react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { CustomButton } from '../components/CustomButton/CustomButton';
import { CustomLink } from '../components/CustomLink/CustomLink';
import { AnimatedModal } from 'components/AnimatedModal/AnimatedModal';
import { IconComponent } from 'components/Icon/IconComponent';
import SliderMotion from 'components/Motion/SliderMotion';

import { selectCandidateApplication } from 'api/candidate-application-slice';
import { useSetHistoryLinkCandidateByTokenMutation } from 'api/candidates-api';

import styles from './CandidateIntroduction.module.scss';

const CandidateIntroduction = () => {
  const navigate = useNavigate();

  const { token } = useParams();

  const {
    application: { candidate, brand, candidate_checks, forms, check_types },
  } = useSelector(selectCandidateApplication);

  const [setHistoryLinkCandidate] = useSetHistoryLinkCandidateByTokenMutation();

  const [utm_source] = useQueryParams(['utm_source']);
  const [admin] = useQueryParams(['admin']);
  const [showIntroduction, setShowIntroduction] = useState(true);

  // Helper to build navigation path with admin parameter preserved
  const buildNavPath = path => buildNavPathHelper(path, admin);

  // Save utm_source
  useEffect(() => {
    if (!utm_source) return;

    setHistoryLinkCandidate(token);
  }, [utm_source, token]);

  const getQuestionnaireFormTitle = candidate_check => {
    if (candidate_check.details.form_id) {
      const form_id = candidate_check.details.form_id;
      const form = forms.find(form => form.id === form_id);
      return form.name;
    } else {
      return candidate_check?.sub_checks?.map(subCheck => subCheck?.title).join(', ');
    }
  };

  // const onRedirect = () => {
  //   const screen =
  //     idVerification?.required && !idVerification?.completed
  //       ? `${CandidateCheckScreens.ID_VERIFICATION}/introduction`
  //       : CandidateCheckScreens.MENU;

  //   navigate(`/form_submission/candidate/${screen}/${token}`, { replace: true });
  // };

  const onRedirect = () => {
    navigate(buildNavPath(`/form_submission/candidate/dashboard/${token}`));
  };

  return (
    <CandidatePage token={token} withTopLogo brand={brand}>
      <AnimatedModal visible={showIntroduction} small noClose title="Introduction to Checkmate">
        <p className="u-margin-top--large">
          Checkmate is a secure verification platform designed to simplify the process for employers
          and employees. The privacy and security of your data is our highest priority, and we have
          several systems and processes to keep your information safe and secure.
          <br />
          <br />
          Checkmate is ISO27001 Certified and uses encrypted connections to ensure your privacy and
          confidentiality is maintained throughout this application. We are externally audited
          annually and commission regular independent tests to ensure the security of our system.
          <br />
          <br />
          Checkmate performs several employment checks and verification on behalf of employers; we
          will list the checks we are processing with you on the next page and request your signed
          consent upon completion.
          <br />
          <br />
          All information you supply is kept strictly confidential and is only accessible by
          authorised staff.
          <br />
          <br />
          Before proceeding, please read our{' '}
          <CustomLink
            brand={brand}
            href="https://www.checkmate.tech/utility/candidate-consent-statement">
            Candidate Consent Statement
          </CustomLink>
          ,{' '}
          <CustomLink brand={brand} href="https://www.checkmate.tech/utility/privacy-policy">
            Privacy Policy
          </CustomLink>{' '}
          and{' '}
          <CustomLink brand={brand} href="https://www.checkmate.tech/utility/terms-and-conditions">
            Terms and Conditions
          </CustomLink>{' '}
          here. By closing this window, you confirm that you have read, understood and agree to
          proceed.
          <br />
          <br />
          If you have any questions or concerns please contact our support team via email at{' '}
          <CustomLink brand={brand} href="mailto:team@checkmate.tech">
            team@checkmate.tech
          </CustomLink>
          .
        </p>
        <CustomButton
          brand={brand}
          small
          onClick={() => setShowIntroduction(false)}
          className="u-margin-top--large">
          Continue
        </CustomButton>
      </AnimatedModal>

      {!showIntroduction && (
        <CandidatePage.View>
          <CandidatePage.Card>
            <CandidatePage.Header
              brand={brand}
              showLogo={false}
              candidateName={candidate?.first_name}
              subtitle={
                <>
                  Welcome to Checkmate! This system is a secure verification platform designed to
                  simplify the process for employers and employees.
                  <br />
                  <br />
                  In this application, we will be performing the following checks:
                </>
              }
            />
            <AnimatePresence mode="wait">
              <SliderMotion key={candidate?.first_name} duration={0.6} isBackward={false}>
                <div
                  className={cn(
                    'u-width-100',
                    'card',
                    'card-with-border',
                    'background-secondary',
                    styles.checkTypesWrapper,
                  )}>
                  {candidate_checks
                    .filter(cc => !cc.details?.bypassed)
                    .map(candidate_check => {
                      const check_type = check_types.find(ct => ct.slug === candidate_check.type);
                      return (
                        <div
                          key={check_type?.slug}
                          className={cn(
                            'd-flex',
                            'align-items-center',
                            'u-gap-medium',
                            styles.checkType,
                          )}>
                          <IconComponent icon={getCheckTypeIcon(check_type?.slug)} />
                          <div>
                            <h4 className={cn('title-4')}>
                              {candidate_check.type === 'candidate_questionnaire'
                                ? getQuestionnaireFormTitle(candidate_check)
                                : check_type?.title}
                            </h4>
                          </div>
                        </div>
                      );
                    })}
                </div>

                <div className="d-flex">
                  <CustomButton
                    type="secondary"
                    className="u-margin-right--small"
                    brand={brand}
                    onClick={() => window.location.replace('https://www.checkmate.tech')}>
                    Close
                  </CustomButton>
                  <CustomButton brand={brand} className="u-margin-left--small" onClick={onRedirect}>
                    Get Started
                  </CustomButton>
                </div>
              </SliderMotion>
            </AnimatePresence>
          </CandidatePage.Card>
        </CandidatePage.View>
      )}
    </CandidatePage>
  );
};

export default CandidateIntroduction;
